import html2pdf from "html2pdf.js";
import moment from "moment";

const isImageURL = (str) => {
  if (typeof str !== "string") return false;
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
  const cloudinaryPattern = /cloudinary\.com/i;
  return imageExtensions.test(str) || cloudinaryPattern.test(str);
};

const areAllImageURLs = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.every((item) => typeof item === "string" && isImageURL(item));
};

const IMAGE_STYLE =
  "display: block; max-width: 500px; max-height: 500px; width: auto; height: auto; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; padding: 5px; background: #fff;";

const waitForImages = (container) => {
  const images = container.querySelectorAll("img");
  if (!images.length) return Promise.resolve();

  return Promise.all(
    Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalHeight > 0) {
            resolve();
            return;
          }
          img.onload = resolve;
          img.onerror = resolve;
        })
    )
  );
};

export const generateFormPDF = (formData, onSuccess, onError) => {
  try {
    const formTitle = formData.customFormId?.title || formData.name || "Form Submission";
    const workerName = formData.workerId
      ? `${formData.workerId.firstName || ""} ${formData.workerId.lastName || ""}`.trim() || "N/A"
      : "N/A";
    const workerEmail = formData.workerId?.email || "N/A";
    const submissionDate = formData.submittedAt
      ? moment(formData.submittedAt).format("DD-MMM-YYYY HH:mm")
      : "N/A";

    let formValuesHTML = "";
    if (formData.values && typeof formData.values === "object") {
      const values = formData.values;
      const fieldKeys = Object.keys(values);

      fieldKeys.forEach((fieldLabel) => {
        const fieldValue = values[fieldLabel];
        let fieldHTML = "";

        if (Array.isArray(fieldValue) && areAllImageURLs(fieldValue)) {
          fieldHTML = `
            <div style="display: flex; flex-direction: column; gap: 12px; margin-top: 8px;">
              ${fieldValue
                .map(
                  (imageUrl) => `
                <img
                  src="${imageUrl}"
                  alt="${fieldLabel}"
                  style="${IMAGE_STYLE}"
                  onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"
                />
                <span style="display: none; color: #999; font-size: 11px;">Image failed to load</span>
              `
                )
                .join("")}
            </div>
          `;
        } else if (typeof fieldValue === "string" && isImageURL(fieldValue)) {
          fieldHTML = `
            <div style="margin-top: 5px;">
              <img
                src="${fieldValue}"
                alt="${fieldLabel}"
                style="${IMAGE_STYLE}"
                onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"
              />
              <span style="display: none; color: #999; font-size: 11px;">Image failed to load</span>
            </div>
          `;
        } else {
          let displayValue = "N/A";
          if (Array.isArray(fieldValue)) {
            displayValue = fieldValue.length > 0 ? fieldValue.join(", ") : "No selection";
          } else if (fieldValue !== null && fieldValue !== undefined && fieldValue !== "") {
            displayValue = String(fieldValue);
          }
          fieldHTML = `<div style="color: #666; padding-left: 10px; font-size: 12px;">${displayValue}</div>`;
        }

        formValuesHTML += `
          <div style="margin-bottom: 15px;">
            <div style="font-weight: bold; margin-bottom: 5px; color: #333; font-size: 14px;">${fieldLabel}:</div>
            ${fieldHTML}
          </div>
        `;
      });
    } else {
      formValuesHTML = '<div style="color: #999;">No form values available</div>';
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 30px;
              color: #333;
              line-height: 1.6;
            }
            .header {
              margin-bottom: 25px;
            }
            .title {
              font-size: 24px;
              font-weight: bold;
              color: #225B27;
              margin-bottom: 20px;
            }
            .info-section {
              margin-bottom: 20px;
              padding: 15px;
              background-color: #f9f9f9;
              border-left: 3px solid #225B27;
            }
            .info-item {
              margin-bottom: 8px;
              font-size: 12px;
            }
            .info-label {
              font-weight: bold;
              color: #555;
            }
            .divider {
              height: 1px;
              background-color: #ddd;
              margin: 20px 0;
            }
            .form-details {
              margin-top: 20px;
            }
            .form-details-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 15px;
              color: #225B27;
            }
            img {
              display: block;
              page-break-inside: avoid;
            }
            .footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #ddd;
              text-align: center;
              font-size: 10px;
              color: #999;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${formTitle}</div>
          </div>

          <div class="info-section">
            <div class="info-item">
              <span class="info-label">Submitted By:</span> ${workerName}
            </div>
            <div class="info-item">
              <span class="info-label">Email:</span> ${workerEmail}
            </div>
            <div class="info-item">
              <span class="info-label">Submission Date:</span> ${submissionDate}
            </div>
          </div>

          <div class="divider"></div>

          <div class="form-details">
            <div class="form-details-title">Form Details</div>
            ${formValuesHTML}
          </div>

          <div class="footer">
            Generated on ${moment().format("DD-MMM-YYYY HH:mm")}
          </div>
        </body>
      </html>
    `;

    const element = document.createElement("div");
    element.innerHTML = htmlContent;
    document.body.appendChild(element);

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `${formTitle.replace(/\s+/g, "_")}_${workerName.replace(/\s+/g, "_")}_${moment(formData.submittedAt || new Date()).format("YYYY-MM-DD")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    const cleanup = () => {
      if (document.body.contains(element)) {
        document.body.removeChild(element);
      }
    };

    waitForImages(element)
      .then(() => html2pdf().set(opt).from(element).save())
      .then(() => {
        cleanup();
        if (onSuccess) onSuccess(opt.filename);
      })
      .catch((error) => {
        cleanup();
        if (onError) onError(error);
      });
  } catch (error) {
    console.error("Error generating PDF:", error);
    if (onError) onError(error);
  }
};
